/**
 * Band Permission Service - Handles band membership and voting permissions
 */

import { fetchWithAuth, API_URL, getCurrentUser } from './ApiService';

class BandPermissionService {
  /**
   * Check if the current user can vote on a setlist
   * @param {string} setlistId - The setlist ID
   * @returns {Promise<boolean>} Whether the user can vote
   */
  async canVoteOnSetlist(setlistId) {
    try {
      // Use the more comprehensive voting eligibility check
      const eligibility = await this.getVotingEligibility(setlistId);
      return eligibility.canVote;
    } catch (error) {
      console.warn('Error checking voting permissions:', error.message);
      return false;
    }
  }

  /**
   * Get user's band memberships
   * @returns {Promise<Array>} Array of band memberships
   */
  async getUserBandMemberships() {
    try {
      const response = await fetchWithAuth(`${API_URL}/bands/memberships`);
      return response.data || [];
    } catch (error) {
      console.error('Error fetching band memberships:', error);
      return [];
    }
  }

  /**
   * Check if user is a member of a specific band
   * @param {string} bandId - The band ID
   * @returns {Promise<boolean>} Whether user is a member
   */
  async isBandMember(bandId) {
    try {
      const memberships = await this.getUserBandMemberships();
      return memberships.some(membership => 
        membership.band_id === bandId && membership.status === 'active'
      );
    } catch (error) {
      console.error('Error checking band membership:', error);
      return false;
    }
  }

  /**
   * Get setlist ownership and band information
   * @param {string} setlistId - The setlist ID
   * @returns {Promise<Object>} Setlist info with ownership data
   */
  async getSetlistPermissionInfo(setlistId) {
    try {
      const response = await fetchWithAuth(`${API_URL}/setlists/${setlistId}`);
      const setlist = response.data?.setlist || response.setlist;
      
      if (!setlist) {
        console.error('No setlist found in response:', response);
        return null;
      }
      
      const currentUserId = await this.getCurrentUserId();
      return {
        setlist,
        isOwner: setlist.owner && setlist.owner._id && currentUserId ? 
          setlist.owner._id === currentUserId : false,
        bandId: setlist.band_id,
        isPersonal: !setlist.band_id
      };
    } catch (error) {
      console.error('Error fetching setlist permission info:', error);
      return null;
    }
  }

  /**
   * Get current user ID from token or storage
   * @returns {string|null} Current user ID
   */
  async getCurrentUserId() {
    try {
      const user = await getCurrentUser();
      return user?._id || null;
    } catch (error) {
      console.error('Error getting current user:', error);
      return null;
    }
  }

  /**
   * Check voting eligibility for a setlist with detailed info
   * @param {string} setlistId - The setlist ID
   * @returns {Promise<Object>} Detailed permission info
   */
  async getVotingEligibility(setlistId) {
    try {
      const permissionInfo = await this.getSetlistPermissionInfo(setlistId);
      if (!permissionInfo) {
        return {
          canVote: false,
          reason: 'Unable to load setlist information'
        };
      }

      const { setlist, isOwner, bandId, isPersonal } = permissionInfo;

      // Personal setlist - only owner can vote
      if (isPersonal) {
        return {
          canVote: isOwner,
          reason: isOwner ? 'You own this setlist' : 'Only the owner can vote on personal setlists',
          setlist
        };
      }

      // Band setlist - check membership
      if (bandId) {
        const isMember = await this.isBandMember(bandId);
        return {
          canVote: isMember,
          reason: isMember ? 'You are a member of this band' : 'Only band members can vote on this setlist',
          setlist,
          bandId
        };
      }

      return {
        canVote: false,
        reason: 'Unknown setlist type',
        setlist
      };

    } catch (error) {
      console.error('Error checking voting eligibility:', error);
      return {
        canVote: false,
        reason: 'Error checking permissions: ' + error.message
      };
    }
  }
}

// Export singleton instance
const bandPermissionService = new BandPermissionService();
export default bandPermissionService;